// Section : Hero Header
// Style : Modern split-screen with gradient background and floating elements
// Brand Colors: Orange #F68521, Teal #1d9eb0, Green #70a553

// import node module libraries
import { Col, Row, Container, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// import media files
import HeroImage from 'assets/images/hero/hero-1.png';
import { PATH_DASHBOARD } from 'routes/Paths';

const HeroHeader = ({isAuth = false}) => {
	return (
		<section className="hero-section position-relative overflow-hidden pt-7 pb-7">
			{/* Background decorative elements */}
			<div className="hero-bg-pattern"></div>
			
			<Container className="py-lg-8 py-6 position-relative">
				<Row className="align-items-center g-5">
					<Col lg={6} md={12}>
						<div className="hero-content">
							{/* Small badge/tag */}
							<div className="hero-badge mb-4">
								<span className="badge-text">Trusted Medical Education Platform</span>
							</div>
							
							<h1 className="hero-title mb-4">
								Empowering Healthcare Excellence
							</h1>
							
							<p className="hero-description mb-5">
								Expert-led courses designed specifically for healthcare professionals 
								and caregivers supporting children and young people with rheumatic diseases.
							</p>
							
							<div className="hero-actions d-flex flex-wrap gap-3">
								<Link
									to={PATH_DASHBOARD.courses.browse}
									className="btn btn-primary btn-lg px-4 py-3 hero-btn-primary"
								>
									<span>Explore Courses</span>
									<i className="fe fe-arrow-right ms-2"></i>
								</Link>
								
								{!isAuth && (
									<Link 
										to="/authentication/sign-in/" 
										className="btn btn-outline-primary btn-lg px-4 py-3 hero-btn-secondary"
									>
										Get Started Free
									</Link>
								)}
							</div>
							
							{/* Trust indicators */}
							{/* <div className="hero-stats mt-5 pt-4">
								<Row className="g-4">
									<Col xs={4}>
										<div className="stat-item">
											<h3 className="stat-number mb-1">500+</h3>
											<p className="stat-label mb-0">Active Learners</p>
										</div>
									</Col>
									<Col xs={4}>
										<div className="stat-item">
											<h3 className="stat-number mb-1">50+</h3>
											<p className="stat-label mb-0">Expert Courses</p>
										</div>
									</Col>
									<Col xs={4}>
										<div className="stat-item">
											<h3 className="stat-number mb-1">95%</h3>
											<p className="stat-label mb-0">Satisfaction</p>
										</div>
									</Col>
								</Row>
							</div> */}
						</div>
					</Col>
					
					<Col lg={6} md={12} className="d-none d-lg-block">
						<div className="hero-image-wrapper">
							<div className="hero-image-container">
								<Image 
									src={HeroImage} 
									alt="Hope Arthritis Academy" 
									className="hero-image"
								/>
								{/* Floating decorative cards */}
								<div className="floating-card floating-card-1">
									<i className="fe fe-award text-primary mb-2"></i>
									<p className="mb-0 small fw-semibold">Certified Programs</p>
								</div>
								<div className="floating-card floating-card-2">
									<i className="fe fe-users text-success mb-2"></i>
									<p className="mb-0 small fw-semibold">Expert Instructors</p>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</Container>

			<style jsx>{`
				.hero-section {
					background: linear-gradient(135deg, #fff9f5 0%, #ffffff 50%, #f0f9f9 100%);
					min-height: 600px;
					display: flex;
					align-items: center;
				}

				.hero-bg-pattern {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					opacity: 0.03;
					background-image: 
						radial-gradient(circle at 20% 30%, #F68521 1px, transparent 1px),
						radial-gradient(circle at 80% 70%, #1d9eb0 1px, transparent 1px);
					background-size: 50px 50px;
					pointer-events: none;
				}

				.hero-badge {
					display: inline-block;
					animation: fadeInUp 0.6s ease-out;
				}

				.badge-text {
					display: inline-block;
					padding: 8px 16px;
					background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
					color: white;
					border-radius: 50px;
					font-size: 13px;
					font-weight: 600;
					letter-spacing: 0.5px;
					text-transform: uppercase;
					box-shadow: 0 4px 15px rgba(246, 133, 33, 0.25);
				}

				.hero-title {
					font-size: clamp(2.5rem, 5vw, 3.75rem);
					font-weight: 800;
					line-height: 1.1;
					color: #1e293b;
					letter-spacing: -0.02em;
					animation: fadeInUp 0.6s ease-out 0.1s backwards;
				}

				.hero-description {
					font-size: 1.125rem;
					line-height: 1.7;
					color: #64748b;
					max-width: 540px;
					animation: fadeInUp 0.6s ease-out 0.2s backwards;
				}

				.hero-actions {
					animation: fadeInUp 0.6s ease-out 0.3s backwards;
				}

				.hero-btn-primary {
					background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
					border: none;
					border-radius: 12px;
					font-weight: 600;
					transition: all 0.3s ease;
					box-shadow: 0 4px 15px rgba(246, 133, 33, 0.3);
				}

				.hero-btn-primary:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 20px rgba(246, 133, 33, 0.4);
					background: linear-gradient(135deg, #ff9d4d 0%, #F68521 100%);
				}

				.hero-btn-secondary {
					border: 2px solid #F68521;
					color: #F68521;
					border-radius: 12px;
					font-weight: 600;
					transition: all 0.3s ease;
					background: white;
				}

				.hero-btn-secondary:hover {
					background: #F68521;
					color: white;
					transform: translateY(-2px);
					box-shadow: 0 6px 20px rgba(246, 133, 33, 0.3);
				}

				.hero-stats {
					border-top: 1px solid #e2e8f0;
					animation: fadeInUp 0.6s ease-out 0.4s backwards;
				}

				.stat-number {
					font-size: 1.75rem;
					font-weight: 800;
					color: #F68521;
					margin-bottom: 0.25rem;
				}

				.stat-label {
					font-size: 0.875rem;
					color: #64748b;
					font-weight: 500;
				}

				.hero-image-wrapper {
					position: relative;
					animation: fadeInRight 0.8s ease-out 0.3s backwards;
				}

				.hero-image-container {
					position: relative;
					padding: 20px;
				}

				.hero-image {
					width: 100%;
					height: auto;
					border-radius: 20px;
					box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
				}

				.floating-card {
					position: absolute;
					background: white;
					padding: 16px 20px;
					border-radius: 12px;
					box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
					display: flex;
					flex-direction: column;
					align-items: center;
					animation: float 3s ease-in-out infinite;
				}

				.floating-card-1 {
					top: 10%;
					right: -10px;
					animation-delay: 0s;
				}

				.floating-card-2 {
					bottom: 15%;
					left: -10px;
					animation-delay: 1.5s;
				}

				.floating-card i {
					font-size: 24px;
				}

				.floating-card .text-primary {
					color: #F68521 !important;
				}

				.floating-card .text-success {
					color: #70a553 !important;
				}

				.floating-card p {
					font-size: 12px;
					white-space: nowrap;
				}

				@keyframes fadeInUp {
					from {
						opacity: 0;
						transform: translateY(30px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}

				@keyframes fadeInRight {
					from {
						opacity: 0;
						transform: translateX(50px);
					}
					to {
						opacity: 1;
						transform: translateX(0);
					}
				}

				@keyframes float {
					0%, 100% {
						transform: translateY(0px);
					}
					50% {
						transform: translateY(-20px);
					}
				}

				@media (max-width: 991px) {
					.hero-section {
						min-height: auto;
					}

					.hero-title {
						font-size: 2.5rem;
					}

					.hero-description {
						font-size: 1rem;
					}

					.hero-stats {
						margin-top: 2rem;
						padding-top: 2rem;
					}
				}
			`}</style>
		</section>
	);
};

export default HeroHeader;